import { Box, Button, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface IProps {
    name: string
    passos: IPasso[]
    /**
     * Posição do overlay em relação ao centro da tela.
     * @example { top: '25%', left: '25%' }
     */
    position?: {
        top?: string
        left?: string
    }
}

export default function OverlayApresentacao({
    name,
    passos,
    position = {},
}: IProps) {
    const useStore = makeStore(name, passos)
    const {
        aberto,
        abrir,
        anterior,
        proximo,
        ignorar,
        isTudoCompleto,
        passoAtual,
    } = useStore()

    useEffect(() => {
        if (
            !isTudoCompleto &&
            aberto == null &&
            !document.getElementById('overlay-apresentacao')
        )
            abrir()
    }, [])

    const zero = {
        width: 0,
        height: 0,
        top: 0,
        left: 0,
    }

    const [dimensoes, setDimensoes] = useState({
        ...zero,
        calculando: false,
    })

    useEffect(() => {
        async function calcularDimensoes() {
            if (passoAtual === 0)
                await new Promise(resolve => setTimeout(resolve, 500))

            if (!passos.at(passoAtual)?.getHighlightElement?.())
                setDimensoes({
                    ...zero,
                    calculando: false,
                })

            setDimensoes({ ...zero, calculando: true })

            const parents = passos.at(passoAtual)?.parentElements || []
            for (let i = 0; i < parents.length; i++) {
                const getElement = parents[i]
                const el = getElement()
                const nextElement =
                    parents[i + 1]?.() ??
                    passos.at(passoAtual)?.getHighlightElement?.()

                if (
                    nextElement?.checkVisibility({ checkVisibilityCSS: true })
                ) {
                    continue
                } else {
                    el?.click()
                }

                await new Promise(resolve => {
                    setTimeout(resolve, 300)
                })
            }

            const elemento = passos.at(passoAtual)?.getHighlightElement?.()
            elemento?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'center',
            })
            await new Promise(resolve => {
                setTimeout(resolve, 300)
            })

            const { width, height, top, left } =
                elemento?.getBoundingClientRect() ?? {}

            setDimensoes({
                width: width ?? 0,
                height: height ?? 0,
                top: top ?? 0,
                left: left ?? 0,
                calculando: false,
            })
        }

        if (passos.at(passoAtual) && !isTudoCompleto) calcularDimensoes()
    }, [passoAtual, isTudoCompleto])

    const { top = '0px', left = '0px' } = position

    return aberto ? (
        <Box
            sx={{
                display: 'flex',
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9999,
                backgroundColor: 'rgba(0,0,0,0.5)',
                color: 'white',
            }}
            id="overlay-apresentacao"
        >
            {!dimensoes.calculando && (
                <Box
                    sx={{
                        backgroundColor: 'rgba(255,255,255,0.35)',
                        width: dimensoes.width,
                        height: dimensoes.height,
                        position: 'absolute',
                        top: dimensoes.top,
                        left: dimensoes.left,
                        borderRadius: '0.3rem',
                        border: '1px solid white',
                    }}
                />
            )}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '25rem',
                    height: 'fit-content',
                    borderRadius: '1rem',
                    p: '0.75rem',
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    backdropFilter: 'blur(5px)',
                    position: 'absolute',
                    top: `calc(50% + ${top})`,
                    left: `calc(50% + ${left})`,
                    transform: 'translate(-50%, -50%)',
                }}
            >
                {typeof passos[passoAtual].title === 'string' ? (
                    <Typography variant="h6">
                        {passos[passoAtual].title}
                    </Typography>
                ) : (
                    passos[passoAtual].title
                )}
                {typeof passos[passoAtual].description === 'string' ? (
                    <Typography padding="1rem">
                        {passos[passoAtual].description}
                    </Typography>
                ) : (
                    passos[passoAtual].description
                )}
                <Box
                    sx={{
                        width: '100%',
                        height: 'fit-content',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: '1rem',
                    }}
                >
                    <Button
                        onClick={() => ignorar()}
                        variant="contained"
                        sx={{
                            justifySelf: 'flex-start',
                            fontSize: '0.8rem',
                            padding: '5px',
                        }}
                    >
                        Ignorar
                    </Button>
                    <Box sx={{ gap: '5px', display: 'flex' }}>
                        <Button
                            onClick={anterior}
                            variant="contained"
                            sx={{
                                fontSize: '0.8rem',
                                padding: '5px',
                            }}
                            disabled={passoAtual === 0 || dimensoes.calculando}
                        >
                            Anterior
                        </Button>
                        <Button
                            onClick={proximo}
                            variant="contained"
                            sx={{
                                fontSize: '0.8rem',
                                padding: '5px',
                            }}
                            disabled={dimensoes.calculando}
                        >
                            {passoAtual === passos.length - 1
                                ? 'Finalizar'
                                : 'Próximo'}
                            {` (${passoAtual + 1}/${passos.length})`}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    ) : null
}

interface PersistState {
    passoAtual: number
    aberto: boolean | null
    isTudoCompleto: boolean
    abrir: () => void
    proximo: () => void
    anterior: () => void
    ignorar: () => void
}

export interface IPasso {
    getHighlightElement?: () => HTMLElement
    parentElements?: Array<() => HTMLElement>
    title: React.ReactNode
    description: React.ReactNode
}

function makeStore(name: string, passos: IPasso[]) {
    return create(
        persist<PersistState>(
            (set, get) => ({
                passoAtual: 0,
                aberto: null,
                isTudoCompleto: get()?.passoAtual > passos.length,
                abrir: () => {
                    set({ aberto: true })
                },
                proximo: () => {
                    const { passoAtual } = get()
                    if (passos.at(passoAtual + 1)) {
                        set({ passoAtual: passoAtual + 1 })
                    } else {
                        set({ aberto: false, isTudoCompleto: true })
                    }
                },
                anterior: () => {
                    const { passoAtual } = get()
                    if (passoAtual > 0) set({ passoAtual: passoAtual - 1 })
                },
                ignorar: () => {
                    set({
                        passoAtual: passos.length + 1,
                        aberto: false,
                        isTudoCompleto: true,
                    })
                },
            }),
            {
                name: `apresentacao/${name}`,
            },
        ),
    )
}
